import React, { useEffect, useState } from 'react';
import { X, Gift, Copy, Check, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'o2_first_visit_coupon_v1';
const COUPON_CODE = 'FIRST200';
const SHOW_DELAY_MS = 15000; // 15s
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const trackConversion = (eventName, extra = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, {
        event_category: 'first_visit_coupon',
        ...extra,
      });
    }
  } catch (e) {
    // ignore analytics failures
  }
};

const FirstVisitCoupon = () => {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState('form'); // 'form' | 'success'
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Skip if the user is on admin dashboard, or already claimed/dismissed.
    if (typeof window === 'undefined') return;
    if (window.location.pathname.startsWith('/o2-control-panel')) return;

    let dismissed = false;
    try {
      dismissed = !!localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      dismissed = false;
    }
    if (dismissed) return;

    const timeout = setTimeout(() => {
      setOpen(true);
      trackConversion('first_visit_popup_shown');
    }, SHOW_DELAY_MS);

    return () => clearTimeout(timeout);
  }, []);

  const persistDismissal = (reason) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ reason, at: new Date().toISOString() })
      );
    } catch (e) {
      // ignore quota errors
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (stage !== 'success') {
      persistDismissal('dismissed');
      trackConversion('first_visit_popup_dismissed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    if (trimmedName.length < 2) {
      setError('Please enter your name');
      return;
    }
    const digits = trimmedPhone.replace(/\D/g, '');
    if (digits.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/notify-telegram`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          phone: trimmedPhone,
          source: `First-Visit Coupon (${COUPON_CODE})`,
        }),
      });
      // Endpoint always returns 200 but log any non-2xx just in case.
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      trackConversion('first_visit_coupon_claimed', { value: 200, currency: 'INR' });
      persistDismissal('claimed');
      setStage('success');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      trackConversion('first_visit_coupon_copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback: select on tap
    }
  };

  if (!open) return null;

  return (
    <div
      className="coupon-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coupon-title"
      data-testid="first-visit-coupon-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="coupon-modal" data-testid="first-visit-coupon-modal">
        <button
          type="button"
          className="coupon-close-btn"
          onClick={handleClose}
          aria-label="Close first-visit offer"
          data-testid="first-visit-coupon-close-btn"
        >
          <X className="coupon-close-icon" />
        </button>

        <div className="coupon-shine" aria-hidden="true" />

        {stage === 'form' && (
          <div className="coupon-body">
            <div className="coupon-icon-badge">
              <Gift className="coupon-gift-icon" />
            </div>
            <p className="coupon-eyebrow">
              <Sparkles className="coupon-eyebrow-icon" />
              First-Visit Exclusive
            </p>
            <h2 id="coupon-title" className="coupon-title">
              Get <span className="coupon-title-amount">₹200 OFF</span>
              <br />
              on Your First Visit
            </h2>
            <p className="coupon-subtitle">
              Drop your details — we'll text your coupon &amp; a stylist will
              hold your slot within minutes.
            </p>

            <form onSubmit={handleSubmit} className="coupon-form" noValidate>
              <input
                type="text"
                className="coupon-input"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                maxLength={80}
                data-testid="first-visit-coupon-name-input"
                aria-label="Your name"
              />
              <input
                type="tel"
                className="coupon-input"
                placeholder="Phone / WhatsApp number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                inputMode="tel"
                maxLength={20}
                data-testid="first-visit-coupon-phone-input"
                aria-label="Phone or WhatsApp number"
              />

              {error && (
                <p className="coupon-error" role="alert" data-testid="first-visit-coupon-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="coupon-submit-btn"
                disabled={submitting}
                data-testid="first-visit-coupon-submit-btn"
              >
                {submitting ? 'Claiming…' : 'Claim My ₹200 OFF'}
              </button>
            </form>

            <p className="coupon-microcopy">
              No spam. Valid on your first visit only. Cannot be combined with Happy Hour.
            </p>
          </div>
        )}

        {stage === 'success' && (
          <div className="coupon-body coupon-success">
            <div className="coupon-icon-badge coupon-icon-badge-success">
              <Check className="coupon-gift-icon" />
            </div>
            <h2 className="coupon-title">You're In! 🎉</h2>
            <p className="coupon-subtitle">
              Your ₹200 OFF is locked in. Show this code at billing:
            </p>

            <button
              type="button"
              className="coupon-code-chip"
              onClick={handleCopy}
              data-testid="first-visit-coupon-code-chip"
              aria-label="Copy coupon code"
            >
              <span className="coupon-code">{COUPON_CODE}</span>
              {copied ? (
                <Check className="coupon-copy-icon" />
              ) : (
                <Copy className="coupon-copy-icon" />
              )}
              <span className="coupon-copy-label">{copied ? 'Copied' : 'Tap to copy'}</span>
            </button>

            <p className="coupon-microcopy">
              We just texted a stylist your details — expect a WhatsApp within
              a few minutes to lock your slot.
            </p>

            <button
              type="button"
              onClick={handleClose}
              className="coupon-submit-btn coupon-submit-btn-secondary"
              data-testid="first-visit-coupon-done-btn"
            >
              Awesome, close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirstVisitCoupon;
